import { getGetSettingsBasicQueryKey, useGetSettingsBasic, usePutSettingsBasic } from '@agos/api-client/platform'
import { apiErrorMessage } from '@agos/api-client'
import { T, tt, useAuth } from '@agos/shared'
import { Spinner, StatusBadge, useToast, BUTTON_PRIMARY, cn } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Save, Settings2, X } from 'lucide-react'
import { useState } from 'react'

import {
  TRACKER_FIELDS,
  clearedTrackers,
  consentLabel,
  mayEditTracking,
  setTrackers,
  trackerLabel,
  trackingChanged,
  trackingFormOf,
  trackingPayload,
  type TrackingForm,
} from './web-tracking'

/**
 * *Web tracking* on the **Tržby** tab - FB-643, §9.359.
 *
 * **Placement is parity, and §9.359 recorded the reason**: `DesignSettingsPage` is the tidier home,
 * but legacy puts this card here (`analytics.tsx:521`, inside `RevenueTab`, right after the period
 * selector), and §0.3's default is to match legacy unless a departure is written down. Moving it later
 * is a smaller change than teaching somebody a new location twice.
 *
 * **Collapsed until asked for, and the read is deferred with it.** The query lives in the component
 * the button mounts, not in this one, so a main admin looking at the revenue tab does not fetch a
 * settings blob to render a dashed button - and the four roles that never see the card never request
 * it either. Mounting is the gate rather than `enabled: open`, because a query that exists but is
 * switched off still needs its flag kept true afterwards; one that is not mounted cannot drift.
 *
 * **Offered to whoever may store it.** Legacy gates the card on `isMainAdmin`, and so did this until §9.758:
 * `PUT /settings/basic` was `@RequireAuth("main_admin")`. BACKEND.md §10A.413 gives its three pixel ids to
 * `settings_general`, so the card asks that right - and the read's role list, which stays `{main_admin, admin}`
 * (`mayEditTracking`). Showing a form nobody can save is the shape FB-001 exists to stop, in both directions.
 */
export function WebTrackingCard() {
  const { user, hasPermission } = useAuth()
  const [open, setOpen] = useState(false)

  // Not a security boundary - the server's is. This stops a form that could only ever be refused.
  if (!mayEditTracking(user?.systemRole, hasPermission)) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-web-tracking-open
        className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl border border-dashed border-border bg-card hover:bg-muted/30 transition-colors text-left"
      >
        <Settings2 className="w-4 h-4 text-muted-foreground" />
        <span className="font-semibold text-foreground text-sm">
          Web tracking - Google Analytics, Meta Pixel, TikTok Pixel
        </span>
        <span className="ml-auto text-xs text-primary">
          <T>Otvoriť</T> ›
        </span>
      </button>
    )
  }

  return <WebTrackingForm onClose={() => setOpen(false)} />
}

function WebTrackingForm({ onClose }: { onClose: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const basic = useGetSettingsBasic()

  if (basic.isLoading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 flex justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <WebTrackingFields
      loaded={trackingFormOf(basic.data)}
      onClose={onClose}
      onSaved={() => {
        // `SettingsGeneralPage` reads the same section and goes stale the moment this saves.
        //
        // **The read that actually serves these ids is not invalidated here and cannot be**:
        // `GET /settings/footer` is what `customer-consent/src/trackers.ts` loads them from, in a
        // different app in a different browser. A changed id reaches a customer on their next
        // document load, which is also when a tracker could next be injected - so there is nothing
        // to arrange, and pretending otherwise by invalidating a key this app never reads would be
        // a line that looks like it does something.
        void queryClient.invalidateQueries({ queryKey: getGetSettingsBasicQueryKey() })
        toast({ title: tt('Uložené'), description: tt('Sledovanie webu bolo uložené.') })
      }}
    />
  )
}

/**
 * The form, over a value the query has already answered.
 *
 * Separated so `useState` initialises from real data once, rather than from `EMPTY_TRACKING` and then
 * having to be resynchronised in an effect - the bug where a slow answer overwrites what somebody has
 * already typed, or never lands at all.
 */
function WebTrackingFields({
  loaded,
  onClose,
  onSaved,
}: {
  loaded: TrackingForm
  onClose: () => void
  onSaved: () => void
}) {
  const [saved, setSaved] = useState<TrackingForm>(loaded)
  const [form, setForm] = useState<TrackingForm>(loaded)
  const put = usePutSettingsBasic()

  const live = setTrackers(form)
  const clearing = clearedTrackers(saved, form)
  const changed = trackingChanged(saved, form)

  const save = async () => {
    const payload = trackingPayload(form)
    try {
      // Spread into a literal: the generated body is an index signature because the spec does not
      // describe it, and a named type is not assignable to one. The precise type stays on the payload.
      await put.mutateAsync({ data: { ...payload } })
      setSaved(payload)
      setForm(payload)
      onSaved()
    } catch {
      // The message is rendered from `put.error` below, which is §9.358's rule: the server's own
      // refusal, not a guess about what caused it.
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-web-tracking>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <Settings2 className="w-4 h-4" /> Web tracking
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            <T>
              Skripty sa na zákaznícku stránku vkladajú až po súhlase v cookie lište - každý čaká na
              inú kategóriu.
            </T>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge
            color={live.length === 0 ? 'zinc' : 'emerald'}
            label={
              live.length === 0
                ? tt('Nič sa nemeria')
                : live.map((key) => trackerLabel(key).split(' ')[0]).join(', ')
            }
            title={
              live.length === 0
                ? tt('Žiadne ID nie je vyplnené, takže stránka nevkladá nič.')
                : tt('Vyplnené ID. Vloží sa až po súhlase v cookie lište.')
            }
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 -m-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={tt('Zatvoriť')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {TRACKER_FIELDS.map((field) => (
        <div key={field.key} className="space-y-1.5">
          <label
            htmlFor={`tracking-${field.key}`}
            className="text-xs font-semibold text-foreground flex items-center gap-2"
          >
            {field.label}
            {field.doc && (
              <a
                href={field.doc}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-0.5 text-[10px]"
              >
                <ExternalLink className="w-3 h-3" />
                <T>Kde ho nájdem?</T>
              </a>
            )}
          </label>
          <input
            id={`tracking-${field.key}`}
            name={field.key}
            value={form[field.key]}
            placeholder={field.placeholder}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) =>
              setForm((current) => ({ ...current, [field.key]: event.target.value }))
            }
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm font-mono outline-none focus:ring-2 focus:ring-primary/20"
          />
          <p className="text-[10px] text-muted-foreground">
            {field.help} <T>Kategória</T>: {consentLabel(field.consent)}.
          </p>
        </div>
      ))}

      {clearing.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400" data-web-tracking-clearing>
          <T>Prázdne pole sledovanie vypne</T>: {clearing.map(trackerLabel).join(', ')}.{' '}
          <T>Zákaznícka stránka ho po uložení prestane vkladať.</T>
        </p>
      )}

      {put.isError && (
        <p className="text-xs text-destructive" data-web-tracking-error>
          {apiErrorMessage(put.error, tt('Nepodarilo sa uložiť.'))}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => void save()}
          disabled={put.isPending || !changed}
          data-web-tracking-save
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm',
            BUTTON_PRIMARY,
          )}
        >
          {put.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          <T>Uložiť</T>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-xl border border-border text-muted-foreground font-semibold text-sm hover:bg-muted transition-colors"
        >
          <T>Zavrieť</T>
        </button>
      </div>
    </div>
  )
}
